import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { getRouteMeta, PRERENDER_ROUTES, type PrerenderRoute } from '@/lib/seo-metadata'

/** Tolerates a trailing slash (`/about/`) — some static hosts normalize to it. */
function normalizeRoute(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

function isPrerenderRoute(pathname: string): pathname is PrerenderRoute {
  return (PRERENDER_ROUTES as readonly string[]).includes(normalizeRoute(pathname))
}

/**
 * The server-rendered `<title>`/description (scripts/prerender.mjs) only
 * covers the very first load of a prerendered route — React Router never
 * reloads the document on client-side navigation, so without this a visitor
 * who clicks from "/" to "/about" keeps whatever title the first page
 * shipped with. Reuses the same route→copy mapping the prerender step uses,
 * so the two can never drift (PR-115/AC-092).
 */
export function useDocumentTitle(): void {
  const { pathname } = useLocation()
  const { i18n } = useTranslation()

  useEffect(() => {
    const route = normalizeRoute(pathname)
    if (!isPrerenderRoute(route)) return
    const { title, description } = getRouteMeta(route, i18n)
    document.title = title
    document.querySelector('meta[name="description"]')?.setAttribute('content', description)
  }, [pathname, i18n, i18n.language])
}
